import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { App as AntApp, Modal } from "antd";
import {
  DownloadOutlined,
  UndoOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  TruckOutlined,
  ArrowLeftOutlined,
  ShoppingOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SkeletonOrderDetails from "../components/SkeletonOrderDetails";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const Wrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px 12px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 24px;
  transition: color 0.3s;
  padding: 0;

  &:hover {
    color: #000;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #000;
  margin: 0 0 32px 0;

  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const OrderInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    padding: 12px;
  }
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  h4 {
    font-size: 11px;
    font-weight: 600;
    color: #999;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  p {
    font-size: 15px;
    font-weight: 600;
    color: #000;
    margin: 0;
  }

  span {
    font-size: 13px;
    color: #666;
  }

  @media (max-width: 480px) {
    h4 {
      font-size: 10px;
    }

    p {
      font-size: 14px;
    }

    span {
      font-size: 12px;
    }
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${(props) => {
    switch (props.status) {
      case "delivered":
      case "Delivered":
        return "#e8f5e9";
      case "processing":
      case "Processing":
        return "#fff3e0";
      case "placed":
      case "In Transit":
        return "#e3f2fd";
      case "cancelled":
      case "Cancelled":
        return "#ffebee";
      case "payment_pending":
        return "#fff8e1";
      default:
        return "#f5f5f5";
    }
  }};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => {
    switch (props.status) {
      case "delivered":
      case "Delivered":
        return "#2e7d32";
      case "processing":
      case "Processing":
        return "#f57c00";
      case "placed":
      case "In Transit":
        return "#1565c0";
      case "cancelled":
      case "Cancelled":
        return "#c62828";
      case "payment_pending":
        return "#f57f17";
      default:
        return "#666";
    }
  }};
  width: fit-content;

  svg {
    font-size: 13px;
  }
`;

// Timeline Styles for In Transit
const TimelineContainer = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 32px 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 24px 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
    margin-bottom: 20px;
  }
`;

const TimelineTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

const Timeline = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  position: relative;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
  }

  &::before {
    content: "";
    position: absolute;
    top: 24px;
    left: 0;
    right: 0;
    height: 3px;
    background: #ddd;
    z-index: 0;

    @media (max-width: 768px) {
      top: 24px;
      height: 2px;
    }

    @media (max-width: 480px) {
      top: 16px;
      height: 2px;
    }
  }
`;

const TimelineStep = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 12px;

  &:not(:last-child) {
    &::after {
      content: "";
      position: absolute;
      top: 24px;
      right: -50%;
      width: ${(props) => (props.completed ? "100%" : "0%")};
      height: 3px;
      background: #667eea;
      transition: width 0.5s ease;
      z-index: -1;

      @media (max-width: 768px) {
        height: 2px;
      }

      @media (max-width: 480px) {
        top: 16px;
        height: 2px;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 0;

    &:nth-child(3)::after,
    &:nth-child(4)::after {
      display: none;
    }
  }

  @media (max-width: 480px) {
    padding: 0 4px;

    &:nth-child(3)::after,
    &:nth-child(4)::after {
      display: ${(props) => (props.completed ? "block" : "none")};
    }
  }
`;

const TimelineCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${(props) => (props.completed ? "#667eea" : "#f0f0f0")};
  border: 3px solid ${(props) => (props.completed ? "#667eea" : "#ddd")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${(props) => (props.completed ? "#fff" : "#999")};
  margin-bottom: 12px;
  transition: all 0.3s ease;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  flex-shrink: 0;

  &:hover {
    ${(props) => props.clickable && "transform: scale(1.1);"}
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 16px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
    margin-bottom: 0;
    border-width: 2px;
  }
`;

const TimelineLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.completed ? "#000" : "#999")};
  margin: 0;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    font-weight: 500;
  }
`;

const TimelineDate = styled.p`
  font-size: 12px;
  color: #999;
  margin: 4px 0 0 0;

  @media (max-width: 768px) {
    font-size: 11px;
  }

  @media (max-width: 480px) {
    font-size: 9px;
    margin: 2px 0 0 0;
  }
`;

// Shipping Details Accordion
const ShippingAccordionContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #eee;
  border-top: none;
  border-radius: 0 0 8px 8px;
  margin-bottom: 32px;
  max-height: ${(props) => (props.isExpanded ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const ShippingDetailsList = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
  }
`;

const ShippingDetail = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const DetailDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.completed ? "#52c41a" : "#d9d9d9")};
  margin-top: 6px;
  flex-shrink: 0;
`;

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const DetailTime = styled.div`
  font-size: 12px;
  color: #999;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

// Detailed Shipping Tracking
const ShippingTrackingContainer = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const TrackingTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 20px;
    color: #667eea;
  }
`;

const ShippingHubsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShippingHub = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr;
  gap: 16px;
  padding: 16px;
  background: ${(props) => (props.completed ? "#f0f7ff" : "#f9f9f9")};
  border: 1px solid ${(props) => (props.completed ? "#e0eeff" : "#eee")};
  border-radius: 6px;
  position: relative;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 12px;
  }

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 24px;
    bottom: -16px;
    width: 2px;
    height: 16px;
    background: ${(props) => (props.completed ? "#667eea" : "#ddd")};
  }
`;

const HubCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${(props) => (props.completed ? "#667eea" : "#f0f0f0")};
  border: 3px solid ${(props) => (props.completed ? "#667eea" : "#ddd")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${(props) => (props.completed ? "#fff" : "#999")};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
`;

const HubInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HubName = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${(props) => (props.completed ? "#000" : "#999")};
  margin: 0 0 4px 0;
`;

const HubLocation = styled.p`
  font-size: 12px;
  color: #999;
  margin: 0 0 6px 0;
`;

const HubTime = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.completed ? "#667eea" : "#999")};
  margin: 0;
`;

const CurrentStatus = styled.div`
  background: #fff3e0;
  border-left: 4px solid #f57c00;
  padding: 16px;
  border-radius: 4px;
  margin-top: 16px;
`;

const StatusText = styled.p`
  font-size: 13px;
  color: #333;
  margin: 0;
  line-height: 1.6;

  strong {
    color: #000;
    font-weight: 600;
  }
`;

const PaymentPendingNote = styled.div`
  background: #fff8e1;
  border-left: 4px solid #f57f17;
  padding: 12px 16px;
  border-radius: 4px;
  margin-top: 12px;
  display: ${(props) => (props.show ? "block" : "none")};
  font-size: 12px;
  color: #333;
  line-height: 1.6;

  strong {
    color: #f57f17;
    font-weight: 600;
  }
`;

// Items and Address Section
const Section = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 16px;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OrderItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #eee;

  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 12px;
    padding: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 70px 1fr;
    gap: 10px;
    padding: 10px;
  }
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 4px;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h3 {
    font-size: 15px;
    font-weight: 600;
    color: #000;
    margin: 0 0 4px 0;
  }

  p {
    font-size: 13px;
    color: #666;
    margin: 0 0 2px 0;
  }

  .qty-price {
    display: flex;
    gap: 16px;
    margin-top: 8px;

    span {
      font-size: 14px;
      font-weight: 600;
      color: #000;
    }
  }

  @media (max-width: 480px) {
    h3 {
      font-size: 13px;
    }

    p {
      font-size: 12px;
    }

    .qty-price {
      gap: 12px;
      margin-top: 6px;

      span {
        font-size: 12px;
      }
    }
  }
`;

const AddressSection = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  padding: 20px;
  border-radius: 6px;

  @media (max-width: 480px) {
    padding: 14px;
  }
`;

const AddressLabel = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 16px;
    color: #667eea;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

const AddressText = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.8;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 1.6;
  }
`;

const PricingSummary = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    margin-bottom: 20px;
  }
`;

const PricingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
    padding-top: 16px;
    padding-bottom: 0;
    font-size: 16px;
    font-weight: 700;
    color: #000;
  }

  &:first-child {
    padding-top: 0;
  }

  span {
    color: #666;

    &.value {
      color: #000;
      font-weight: 600;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  background: ${(props) => (props.secondary ? "#f0f0f0" : "#000")};
  color: ${(props) => (props.secondary ? "#000" : "#fff")};
  border: ${(props) => (props.secondary ? "1px solid #ddd" : "none")};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${(props) => (props.secondary ? "#e0e0e0" : "#333")};
  }

  svg {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 11px;
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  display: ${(props) => (props.open ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 24px;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  color: #999;
  transition: color 0.3s;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #000;
  }
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin: 0 0 24px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #000;
  margin-bottom: 8px;
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.3s;

  &:hover,
  &:focus {
    border-color: #667eea;
    outline: none;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  background: #fff;
  transition: border-color 0.3s;

  &:hover,
  &:focus {
    border-color: #667eea;
    outline: none;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  background: #fff;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border-color 0.3s;

  &:hover,
  &:focus {
    border-color: #667eea;
    outline: none;
  }
`;

const OptionGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const OptionButton = styled.button`
  padding: 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: ${(props) => (props.active ? "#667eea" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#000")};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #667eea;
    background: ${(props) => (props.active ? "#667eea" : "#f9f9f9")};
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  background: #f9f9f9;
  transition: all 0.3s;

  &:hover {
    border-color: #667eea;
    background: #f0f4ff;
  }

  input {
    display: none;
  }
`;

const ImageUploadText = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImagePreviewItem = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #eee;
  overflow: hidden;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ff4444;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.3s;
  z-index: 1;

  &:hover {
    background: #cc0000;
  }
`;

const ReviewButton = styled.button`
  padding: 6px 12px;
  background: black;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 8px;
  border: 1px solid black;
  &:hover {
    background: white;
    color: black;
    border: 1px solid black;
  }
`;

const RatingSelector = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  span {
    cursor: pointer;
    font-size: 28px;
    transition: transform 0.2s ease;
    color: #ddd;

    &:hover {
      transform: scale(1.2);
    }

    &.active {
      color: #ffc107;
    }
  }
`;

const ReviewTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }

  &::placeholder {
    color: #999;
  }
`;

const ImageUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.label`
  padding: 10px 16px;
  border: 2px dashed #ddd;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  font-weight: 500;
  color: #666;

  &:hover {
    border-color: #667eea;
    color: #667eea;
  }
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ReviewRemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: red;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.2s ease;

  &:hover {
    background: darkred;
    transform: scale(1.1);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  ${(props) =>
    props.primary
      ? `
    background: #000;
    color: #fff;
    
    &:hover {
      background: #333;
    }
  `
      : `
    background: #f0f0f0;
    color: #000;
    
    &:hover {
      background: #e0e0e0;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReviewImagesContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  margin-bottom: 8px;
`;

const ReviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #f0f0f0;

  &:hover {
    border-color: #fd8e07;
    transform: scale(1.05);
  }
`;

const ShowAllReviewsButton = styled.button`
  background: #fd8e07;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  width: 100%;
  transition: all 0.3s ease;

  &:hover {
    background: #fc8c04;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(253, 142, 7, 0.3);
  }
`;

const SlideshowContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .slideshow-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 450px;
    padding: 20px;
    background: #f5f5f5;
  }

  .slide-image {
    max-width: 350px;
    max-height: 350px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
  }

  .nav-buttons {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.6);
    border: none;
    color: white;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    font-weight: bold;
    transition: all 0.3s ease;
    z-index: 10;

    &:hover {
      background: rgba(0, 0, 0, 0.9);
      transform: translateY(-50%) scale(1.1);
    }

    &:active {
      transform: translateY(-50%) scale(0.95);
    }
  }

  .nav-prev {
    left: 15px;
  }

  .nav-next {
    right: 15px;
  }

  .slide-counter {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
  }

  .dot-indicators {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }

    &.active {
      background: #fd8e07;
    }
  }

  /* Tablet responsiveness */
  @media (max-width: 768px) {
    max-width: 85%;

    .slideshow-wrapper {
      height: 320px;
      padding: 12px;
    }

    .slide-image {
      max-width: 250px;
      max-height: 250px;
    }

    .nav-buttons {
      width: 36px;
      height: 36px;
      font-size: 16px;
    }

    .nav-prev {
      left: 8px;
    }

    .nav-next {
      right: 8px;
    }

    .slide-counter {
      font-size: 11px;
      padding: 5px 10px;
      bottom: 12px;
    }

    .dot {
      width: 9px;
      height: 9px;
    }
  }

  /* Mobile responsiveness */
  @media (max-width: 600px) {
    max-width: 100%;
    margin: 0;
    border-radius: 0;

    .slideshow-wrapper {
      height: 240px;
      padding: 8px;
    }

    .slide-image {
      max-width: 180px;
      max-height: 180px;
    }

    .nav-buttons {
      width: 30px;
      height: 30px;
      font-size: 14px;
    }

    .nav-prev {
      left: 6px;
    }

    .nav-next {
      right: 6px;
    }

    .slide-counter {
      font-size: 10px;
      padding: 4px 8px;
      bottom: 10px;
    }

    .dot {
      width: 7px;
      height: 7px;
      gap: 4px;
    }
  }

  /* Small mobile devices */
  @media (max-width: 400px) {
    .slideshow-wrapper {
      height: 200px;
      padding: 6px;
    }

    .slide-image {
      max-width: 150px;
      max-height: 150px;
    }

    .nav-buttons {
      width: 28px;
      height: 28px;
      font-size: 12px;
    }

    .nav-prev {
      left: 4px;
    }

    .nav-next {
      right: 4px;
    }

    .slide-counter {
      font-size: 9px;
      padding: 3px 6px;
      bottom: 8px;
    }

    .dot {
      width: 6px;
      height: 6px;
    }
  }
`;

const ProductSelectionBox = styled.div`
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  background: #f9f9f9;

  .product-info {
    display: flex;
    gap: 12px;
    align-items: center;

    img {
      width: 60px;
      height: 60px;
      object-fit: contain;
      border-radius: 4px;
    }

    .details {
      flex: 1;

      p {
        font-size: 13px;
        margin: 0 0 4px 0;
        color: #000;
        font-weight: 600;

        &.meta {
          font-size: 12px;
          color: #999;
          font-weight: 400;
        }
      }
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;
  margin-bottom: 20px;

  .step-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }
`;

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
    case "Delivered":
      return <CheckCircleOutlined />;
    case "processing":
    case "Processing":
      return <ClockCircleOutlined />;
    case "placed":
    case "In Transit":
      return <TruckOutlined />;
    case "payment_pending":
      return <ClockCircleOutlined />;
    default:
      return null;
  }
};

const getStatusLabel = (status) => {
  const statusMap = {
    delivered: "Delivered",
    placed: "Placed",
    processing: "Processing",
    cancelled: "Cancelled",
    payment_pending: "Payment Pending",
  };
  return statusMap[status] || status;
};

const isReturnWindowValid = (order) => {
  if (!order) return false;

  // Check if status is delivered
  const isDelivered =
    order.status === "delivered" || order.status === "Delivered";
  if (!isDelivered) return false;

  // Check if order is within 8-day return window
  if (!order.createdAt) return false;

  const now = new Date();
  const orderDate = new Date(order.createdAt);
  const daysElapsed = (now - orderDate) / (1000 * 60 * 60 * 24);

  return daysElapsed <= 8;
};

const OrderDetails = () => {
  const { id, orderNumber } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const userId = useSelector((state) => state.userId);
  const { message } = AntApp.useApp();

  const [order, setOrder] = useState(null);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Return/Replace Modal State
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnFlow, setReturnFlow] = useState(null); // 'return' or 'replace'
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemData, setSelectedItemData] = useState(null); // Raw item data from API
  const [returnReason, setReturnReason] = useState("");
  const [replaceReason, setReplaceReason] = useState(""); // 'size_color' or 'damaged'
  const [reasonText, setReasonText] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [replaceImages, setReplaceImages] = useState([]);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [productData, setProductData] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [ordersReturns, setOrdersReturns] = useState([]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewingItem, setReviewingItem] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewImages, setReviewImages] = useState([]);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [itemReviews, setItemReviews] = useState({}); // { productId: reviewData }
  const [loadingReviewItems, setLoadingReviewItems] = useState(new Set()); // Track which items are loading
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [previewedReviewImages, setPreviewedReviewImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

  // Fetch review for a specific item
  const fetchItemReview = async (productId) => {
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/products/${productId}/reviews`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();

      if (data.status && Array.isArray(data.data)) {
        // Find user's review
        const userReview = data.data.find(
          (review) => review.user?.id === userId,
        );
        if (userReview) {
          setItemReviews((prev) => ({
            ...prev,
            [productId]: userReview,
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching item review:", err);
    } finally {
      // Remove from loading set
      setLoadingReviewItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // Delete item review
  const handleDeleteItemReview = async (reviewId, productId) => {
    if (!token) {
      message.error("Please log in first");
      return;
    }

    Modal.confirm({
      title: "Delete Review",
      content:
        "Are you sure you want to delete this review? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeletingReviewId(reviewId);
          const apiBase = process.env.REACT_APP_API_BASE_URL;
          const response = await fetch(
            `${apiBase}/products/${productId}/reviews/${reviewId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          const data = await response.json();

          if (data.status) {
            // Remove review from state
            setItemReviews((prev) => {
              const newReviews = { ...prev };
              delete newReviews[productId];
              return newReviews;
            });
            message.success("Review deleted successfully");
          } else {
            message.error(data.message || "Failed to delete review");
          }
        } catch (err) {
          console.error("Error deleting review:", err);
          message.error("Error deleting review");
        } finally {
          setDeletingReviewId(null);
        }
      },
      onCancel() {},
    });
  };

  const openImagePreview = (images, initialIndex = 0) => {
    setPreviewedReviewImages(images);
    setSelectedImageIndex(initialIndex);
    setIsImagePreviewOpen(true);
  };

  // Fetch returns data for the order
  useEffect(() => {
    const fetchReturns = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${apiBase}/orders/${id}/returns`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && data.data?.returns) {
          setOrdersReturns(data.data.returns);
        }
      } catch (err) {
        console.error("Error fetching returns:", err);
      }
    };

    if (id && token) {
      fetchReturns();
    }
  }, [id, token]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setIsLoading(true);
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${apiBase}/orders/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && data.data) {
          // Transform API response to match component format
          const apiOrder = data.data;
          const transformedOrder = {
            id: apiOrder.order_number,
            date: new Date(apiOrder.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            createdAt: new Date(apiOrder.created_at),
            status:
              apiOrder.payment_status !== "paid" &&
              apiOrder.order_status === "placed"
                ? "payment_pending"
                : apiOrder.order_status,
            paymentStatus: apiOrder.payment_status,
            totalAmount: parseFloat(apiOrder.total) || 0,
            subtotal: parseFloat(apiOrder.subtotal) || 0,
            shipping: parseFloat(apiOrder.shipping) || 0,
            discount: parseFloat(apiOrder.discount) || 0,
            tax: 0, // API doesn't provide tax, set to 0
            items: apiOrder.items.map((item) => ({
              id: item.id,
              name: item.product?.name || "Product",
              quantity: item.quantity,
              price: parseFloat(item.price) || 0,
              image: item.product?.images?.[0]?.url || "",
              size: item.variant?.size?.name,
              color: item.variant?.color?.name,
              productId: item.product_id,
              variantId: item.product_variant_id,
            })),
            itemsRaw: apiOrder.items, // Store raw items for later use
            address: {
              name: apiOrder.address?.name || "",
              street: apiOrder.address?.address || "",
              city: apiOrder.address?.city || "",
              state: apiOrder.address?.state || "",
              pincode: apiOrder.address?.pincode || "",
              phone: apiOrder.address?.phone || "",
            },
            payment: apiOrder.payment,
          };
          setOrder(transformedOrder);
          setError(null);

          // Initialize loading state with all product IDs
          if (apiOrder.items && apiOrder.items.length > 0) {
            const productIds = new Set(
              apiOrder.items.map((item) => item.product_id),
            );
            setLoadingReviewItems(productIds);

            // Fetch reviews for each item
            apiOrder.items.forEach((item) => {
              fetchItemReview(item.product_id);
            });
          }
        } else {
          setError(data.message || "Failed to fetch order details");
          setOrder(null);
        }
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Error fetching order details. Please try again.");
        setOrder(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (id && token) {
      fetchOrderDetails();
    }

    window.scrollTo(0, 0);
  }, [id, token]);

  const handleShippedClick = () => {
    setShowShippingDetails(!showShippingDetails);
  };

  const handleDownloadInvoice = async () => {
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const invoiceUrl = `${apiBase}/orders/${order.id}/invoice/download`;

      const response = await fetch(invoiceUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download invoice");
      }

      // Create blob from response
      const blob = await response.blob();

      // Check if response is HTML or PDF
      if (blob.type === "text/html" || blob.type.includes("text/html")) {
        // If HTML, open in new window
        const htmlUrl = window.URL.createObjectURL(blob);
        const newWindow = window.open(htmlUrl, "_blank");
        if (newWindow) {
          newWindow.focus();
        }
      } else {
        // If PDF, download as file
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${order.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      message.error("Failed to download invoice. Please try again.");
    }
  };

  const getShippedStep = () => {
    return order?.timeline?.find((step) => step.label === "Shipped");
  };

  const handleReturnModalOpen = () => {
    setShowReturnModal(true);
    setReturnFlow(null);
    setSelectedItem(null);
    setSelectedItemData(null);
    setReturnReason("");
    setReplaceReason("");
    setReasonText("");
    setUploadedImages([]);
    setReplaceImages([]);
    setSelectedSize("");
    setSelectedColor("");
  };

  // Fetch product details with variants when item is selected
  useEffect(() => {
    if (returnFlow === "replace" && selectedItem) {
      const fetchProductDetails = async () => {
        try {
          setProductLoading(true);
          const apiBase = process.env.REACT_APP_API_BASE_URL;

          // Find the raw item data from order
          const rawItemData = order?.itemsRaw?.find(
            (item) => item.id === selectedItem.id,
          );

          if (!rawItemData) {
            console.error("Could not find raw item data");
            return;
          }

          setSelectedItemData(rawItemData);

          // Fetch product with variants
          const response = await fetch(
            `${apiBase}/products/${rawItemData.product_id}`,
          );
          const data = await response.json();

          if (data.status && data.data) {
            setProductData(data.data);

            // Extract available sizes from variants
            const sizesSet = new Set();

            if (data.data.variants) {
              Object.keys(data.data.variants).forEach((size) => {
                if (size) sizesSet.add(size);
              });
            }

            setAvailableSizes(Array.from(sizesSet));

            // Set default selections
            const firstSize = Array.from(sizesSet)[0];
            if (firstSize) {
              setSelectedSize(firstSize);

              // Get colors for first size
              const colorsForFirstSize = data.data.variants?.[firstSize] || [];
              const colorsSet = new Set();

              if (Array.isArray(colorsForFirstSize)) {
                colorsForFirstSize.forEach((colorObj) => {
                  if (colorObj?.color_name) {
                    colorsSet.add(colorObj.color_name);
                  }
                });
              }

              const colorsArray = Array.from(colorsSet);

              setAvailableColors(colorsArray);

              if (colorsArray[0]) {
                setSelectedColor(colorsArray[0]);
              }
            }
          }
        } catch (err) {
          console.error("Error fetching product details:", err);
        } finally {
          setProductLoading(false);
        }
      };

      fetchProductDetails();
    }
  }, [returnFlow, selectedItem, order?.itemsRaw]);

  // Update available colors when selected size changes
  useEffect(() => {
    if (productData && selectedSize) {
      const colorsForSize = productData.variants?.[selectedSize] || [];

      const colorsSet = new Set();

      if (Array.isArray(colorsForSize)) {
        colorsForSize.forEach((colorObj) => {
          if (colorObj?.color_name) {
            colorsSet.add(colorObj.color_name);
          }
        });
      }

      const colorsArray = Array.from(colorsSet);
      setAvailableColors(colorsArray);

      // Set default color for the selected size
      const firstColor = colorsArray[0];
      if (firstColor) {
        setSelectedColor(firstColor);
      } else {
        setSelectedColor("");
      }
    }
  }, [selectedSize, productData]);

  const handleReturnModalClose = () => {
    setShowReturnModal(false);
    // Reset all state
    setTimeout(() => {
      setReturnFlow(null);
      setSelectedItem(null);
      setSelectedItemData(null);
      setReturnReason("");
      setReplaceReason("");
      setReasonText("");
      setUploadedImages([]);
      setReplaceImages([]);
      setSelectedSize("");
      setSelectedColor("");
      setAvailableSizes([]);
      setAvailableColors([]);
      setProductData(null);
    }, 300);
  };

  const handleReturnFlow = (type) => {
    setReturnFlow(type);
    setReplaceReason("");
    setReasonText("");
    if (type === "return") {
      setSelectedItem(null);
      setReasonText("");
    } else if (type === "replace" && order?.items?.length === 1) {
      setSelectedItem(order.items[0]);
    }
    setReplaceImages([]);
    setSelectedSize("");
    setSelectedColor("");
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 4 - uploadedImages.length;

    files.slice(0, remainingSlots).forEach((file) => {
      setUploadedImages((prev) => [...prev, file]);
    });
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReplaceImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 4 - replaceImages.length;

    files.slice(0, remainingSlots).forEach((file) => {
      setReplaceImages((prev) => [...prev, file]);
    });
  };

  const handleRemoveReplaceImage = (index) => {
    setReplaceImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Get return status for an item
  const getReturnStatusForItem = (itemId) => {
    return ordersReturns.find((ret) => ret.order_item_id === itemId);
  };

  // Get status badge styling
  const getStatusBadgeStyle = (status) => {
    const statusMap = {
      refunded: {
        bg: "#d4edda",
        color: "#155724",
        label: "Refunded",
        icon: "✓",
      },
      pending: {
        bg: "#fff3cd",
        color: "#856404",
        label: "Pending",
        icon: "⏳",
      },
      approved: {
        bg: "#cfe2ff",
        color: "#084298",
        label: "Approved",
        icon: "✓",
      },
      rejected: {
        bg: "#f8d7da",
        color: "#842029",
        label: "Rejected",
        icon: "✕",
      },
    };
    return (
      statusMap[status] || {
        bg: "#e2e3e5",
        color: "#383d41",
        label: status,
        icon: "ℹ",
      }
    );
  };

  const handleSubmitReturn = async () => {
    if (!returnReason || !reasonText) {
      message.error("Please fill in all required fields");
      return;
    }

    if (uploadedImages.length === 0) {
      message.error("Please upload at least one image");
      return;
    }

    try {
      const formData = new FormData();

      // Get the selected item or first item
      const itemToReturn = selectedItem || order.items[0];
      const rawItemData = order.itemsRaw.find(
        (item) => item.id === itemToReturn.id,
      );

      if (!rawItemData) {
        message.error("Error: Could not find item details");
        return;
      }

      // Add form fields
      formData.append("order_item_id", itemToReturn.id);
      formData.append("request_type", "return");
      formData.append("product_variant_id", rawItemData.product_variant_id);
      formData.append("reason", `${returnReason} - ${reasonText}`);
      formData.append("refund", "true");

      // Add images
      uploadedImages.forEach((file, index) => {
        formData.append(`product_images[${index}]`, file);
      });

      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/returns`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        message.success("Return request submitted successfully!");
        handleReturnModalClose();
      } else {
        message.error(data.message || "Failed to submit return request");
      }
    } catch (err) {
      console.error("Error submitting return:", err);
      message.error("Error submitting return request. Please try again.");
    }
  };

  const handleImageUploadChange = (e) => {
    const files = Array.from(e.target.files);
    const MAX_IMAGES = 4;

    setReviewImages((prev) => {
      const totalImages = prev.length + files.length;

      if (totalImages > MAX_IMAGES) {
        message.error(
          `Maximum ${MAX_IMAGES} photos allowed. You can only add ${MAX_IMAGES - prev.length} more.`,
        );
        return prev;
      }

      return [...prev, ...files];
    });

    e.target.value = "";
  };

  const handleRemoveReviewImage = (index) => {
    setReviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) {
      message.error("Please write a review");
      return;
    }

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setIsSubmittingReview(true);
      const loadingMsg = message.loading("Submitting your review...", 0);

      const formData = new FormData();
      formData.append("rating", reviewRating);
      formData.append("comment", reviewText);

      // Append images as files
      reviewImages.forEach((image, index) => {
        formData.append(`images[]`, image);
      });

      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(
        `${apiBase}/products/${reviewingItem.productId}/reviews`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const data = await response.json();

      loadingMsg();

      if (data.status) {
        message.success("Review submitted successfully!");
        setIsReviewModalOpen(false);
        setReviewingItem(null);
        setReviewText("");
        setReviewImages([]);
        setReviewRating(5);

        // Fetch the newly submitted review to display it immediately
        if (reviewingItem?.productId) {
          setLoadingReviewItems(
            (prev) => new Set([...prev, reviewingItem.productId]),
          );
          fetchItemReview(reviewingItem.productId);
        }
      } else {
        message.error(data.message || "Failed to submit review");
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      message.error("Error submitting review. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleSubmitReplace = async () => {
    if (!selectedItem) {
      message.error("Please select an item");
      return;
    }

    if (!replaceReason) {
      message.error("Please select a reason");
      return;
    }

    if (replaceReason === "size_color" && (!selectedSize || !selectedColor)) {
      message.error("Please select size and color");
      return;
    }

    if (replaceImages.length === 0) {
      message.error("Please upload at least one image");
      return;
    }

    try {
      const formData = new FormData();

      // Find the variant ID for selected size and color
      let newVariantId = null;
      if (replaceReason === "size_color") {
        const variantsForSize = productData?.variants?.[selectedSize] || [];
        const selectedVariant = variantsForSize.find(
          (v) => v.color_name === selectedColor,
        );
        newVariantId = selectedVariant?.id;

        if (!newVariantId) {
          message.error("Error: Could not find selected variant");
          return;
        }
      }

      const rawItemData = order.itemsRaw.find(
        (item) => item.id === selectedItem.id,
      );

      if (!rawItemData) {
        message.error("Error: Could not find item details");
        return;
      }

      // Construct reason
      let fullReason =
        replaceReason === "size_color"
          ? `Size/Color Issue - Size: ${selectedSize}, Color: ${selectedColor}`
          : `Damaged - ${reasonText}`;

      // Add form fields
      formData.append("order_item_id", selectedItem.id);
      formData.append("request_type", "replace");
      formData.append(
        "product_variant_id",
        newVariantId || rawItemData.product_variant_id,
      );
      formData.append("reason", fullReason);

      // Add images
      replaceImages.forEach((file, index) => {
        formData.append(`product_images[${index}]`, file);
      });

      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/returns`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        message.success("Replacement request submitted successfully!");
        handleReturnModalClose();
      } else {
        message.error(data.message || "Failed to submit replacement request");
      }
    } catch (err) {
      console.error("Error submitting replacement:", err);
      message.error("Error submitting replacement request. Please try again.");
    }
  };

  return (
    <Container>
      {/* <Navbar /> */}
      {isLoading ? (
        <SkeletonOrderDetails />
      ) : !order ? (
        <Wrapper>
          <PageTitle>Order Not Found</PageTitle>
          <ActionButton onClick={() => navigate("/orders")}>
            <ArrowLeftOutlined />
            Back to Orders
          </ActionButton>
        </Wrapper>
      ) : (
        <Wrapper>
          <BackButton onClick={() => navigate("/orders")}>
            <ArrowLeftOutlined />
            Back to Orders
          </BackButton>

          <PageTitle>Order Details</PageTitle>

          {/* Order Info Header */}
          <OrderInfoGrid>
            <InfoBox>
              <h4>Order ID</h4>
              <p>{order.id}</p>
            </InfoBox>

            <InfoBox>
              <h4>Order Date</h4>
              <p>{order.date}</p>
            </InfoBox>

            <InfoBox>
              <h4>Order Status</h4>
              <StatusBadge status={order.status}>
                {getStatusIcon(order.status)}
                {getStatusLabel(order.status)}
              </StatusBadge>
            </InfoBox>

            <InfoBox>
              <h4>Order Total</h4>
              <p>₹{(order.totalAmount || 0).toLocaleString("en-IN")}</p>
            </InfoBox>
          </OrderInfoGrid>

          {/* Payment Pending Note */}
          <PaymentPendingNote show={order.status === "payment_pending"}>
            <strong>⏱️ Payment Pending:</strong> Your payment is being
            processed. It may take up to 30 minutes for the payment status to
            update. If it doesn't update after 30 minutes, please contact
            support.
          </PaymentPendingNote>

          {/* Timeline for In Transit Orders */}
          {order.status === "placed" && order.timeline && (
            <>
              <TimelineContainer>
                <TimelineTitle>Order Status</TimelineTitle>
                <Timeline>
                  {order.timeline.map((step, index) => (
                    <TimelineStep
                      key={index}
                      completed={step.completed}
                      onClick={
                        step.label === "Shipped"
                          ? handleShippedClick
                          : undefined
                      }
                    >
                      <TimelineCircle
                        completed={step.completed}
                        clickable={step.label === "Shipped"}
                      >
                        {step.completed ? (
                          <CheckCircleOutlined style={{ fontSize: "24px" }} />
                        ) : (
                          <ClockCircleOutlined style={{ fontSize: "24px" }} />
                        )}
                      </TimelineCircle>
                      <TimelineLabel completed={step.completed}>
                        {step.label}
                      </TimelineLabel>
                      <TimelineDate>{step.date}</TimelineDate>
                    </TimelineStep>
                  ))}
                </Timeline>
              </TimelineContainer>

              {/* Shipping Details Accordion - Shows when Shipped is clicked */}
              {getShippedStep() && (
                <ShippingAccordionContainer isExpanded={showShippingDetails}>
                  <ShippingDetailsList>
                    {getShippedStep().subSteps?.map((detail, index) => (
                      <ShippingDetail key={index}>
                        <DetailDot completed={detail.completed} />
                        <DetailInfo>
                          <DetailLabel>{detail.label}</DetailLabel>
                          <DetailTime>{detail.time}</DetailTime>
                        </DetailInfo>
                      </ShippingDetail>
                    ))}
                  </ShippingDetailsList>
                </ShippingAccordionContainer>
              )}
            </>
          )}

          {/* Items Section */}
          <Section>
            <SectionTitle>
              <ShoppingOutlined />
              Ordered Items ({(order.items || []).length})
            </SectionTitle>
            <ItemsList>
              {(order.items || []).map((item) => {
                const returnStatus = getReturnStatusForItem(item.id);
                const statusInfo = returnStatus
                  ? getStatusBadgeStyle(returnStatus.status)
                  : null;
                const userReview = itemReviews[item.productId];

                return (
                  <OrderItem key={item.id}>
                    <ItemImage src={item.image} alt={item.name} />
                    <ItemDetails>
                      <h3>{item.name}</h3>
                      <p>Product Item</p>
                      {returnStatus && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px 10px",
                            background: statusInfo.bg,
                            color: statusInfo.color,
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "600",
                            width: "fit-content",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>{statusInfo.icon}</span>
                          <span>
                            {returnStatus.request_type === "return"
                              ? "↩️ Return"
                              : "🔄 Replace"}
                            {" - "}
                            {statusInfo.label}
                          </span>
                        </div>
                      )}
                      <div className="qty-price">
                        <span>Qty: {item.quantity}</span>
                        <span>
                          ₹
                          {((item.price || 0) * item.quantity).toLocaleString(
                            "en-IN",
                          )}
                        </span>
                      </div>

                      {/* Show existing review or write review button */}
                      {order.status === "delivered" ||
                      order.status === "Delivered" ? (
                        loadingReviewItems.has(item.productId) ? (
                          <div
                            style={{
                              background: "#f9f9f9",
                              border: "1px solid #eee",
                              borderRadius: "6px",
                              padding: "12px",
                              marginTop: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                marginBottom: "6px",
                              }}
                            >
                              {[...Array(5)].map((_, i) => (
                                <span
                                  key={i}
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    background: "#e0e0e0",
                                    borderRadius: "2px",
                                    display: "inline-block",
                                    animation:
                                      "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              ))}
                            </div>
                            <div
                              style={{
                                height: "40px",
                                background: "#e0e0e0",
                                borderRadius: "4px",
                                marginTop: "8px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                            <style>{`
                              @keyframes pulse {
                                0%, 100% { opacity: 0.6; }
                                50% { opacity: 1; }
                              }
                            `}</style>
                          </div>
                        ) : userReview ? (
                          <div
                            style={{
                              background: "#f9f9f9",
                              border: "1px solid #eee",
                              borderRadius: "6px",
                              padding: "12px",
                              marginTop: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                marginBottom: "6px",
                                color: "#ffc107",
                              }}
                            >
                              {[...Array(5)].map((_, i) => (
                                <span key={i}>
                                  {i < userReview.rating ? "★" : "☆"}
                                </span>
                              ))}
                            </div>
                            <p
                              style={{
                                margin: "0 0 8px 0",
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              {userReview.comment}
                            </p>
                            {userReview.images &&
                              userReview.images.length > 0 && (
                                <ReviewImagesContainer>
                                  {userReview.images.map((image, idx) => (
                                    <ReviewImage
                                      key={idx}
                                      src={image.url}
                                      alt={`Review image ${idx + 1}`}
                                      onClick={() =>
                                        openImagePreview(userReview.images, idx)
                                      }
                                    />
                                  ))}
                                </ReviewImagesContainer>
                              )}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                                marginTop: "10px",
                              }}
                            >
                              <span style={{ fontSize: "11px", color: "#999" }}>
                                {new Date(
                                  userReview.created_at,
                                ).toLocaleDateString()}
                              </span>
                              <button
                                onClick={() =>
                                  handleDeleteItemReview(
                                    userReview.id,
                                    item.productId,
                                  )
                                }
                                disabled={deletingReviewId === userReview.id}
                                style={{
                                  background: "#dc3545",
                                  color: "white",
                                  border: "none",
                                  padding: "6px 12px",
                                  borderRadius: "4px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  cursor:
                                    deletingReviewId === userReview.id
                                      ? "not-allowed"
                                      : "pointer",
                                  opacity:
                                    deletingReviewId === userReview.id
                                      ? 0.6
                                      : 1,
                                  transition: "all 0.3s ease",
                                }}
                              >
                                {deletingReviewId === userReview.id
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <ReviewButton
                            onClick={() => {
                              setReviewingItem(item);
                              setIsReviewModalOpen(true);
                              setReviewRating(5);
                              setReviewText("");
                              setReviewImages([]);
                            }}
                          >
                            ★ WRITE A REVIEW
                          </ReviewButton>
                        )
                      ) : null}
                    </ItemDetails>
                  </OrderItem>
                );
              })}
            </ItemsList>
          </Section>

          {/* Delivery Address */}
          <Section>
            <SectionTitle>
              <EnvironmentOutlined />
              Delivery Address
            </SectionTitle>
            <AddressSection>
              <AddressLabel>
                <EnvironmentOutlined />
                Delivery Location
              </AddressLabel>
              <AddressText>
                {order.address.name}
                <br />
                {order.address.street}
                <br />
                {order.address.city}, {order.address.state}{" "}
                {order.address.pincode}
                <br />
                <br />
                <strong>Phone:</strong> {order.address.phone}
              </AddressText>
            </AddressSection>
          </Section>

          {/* Pricing Summary */}
          <PricingSummary>
            <SectionTitle style={{ marginBottom: "16px" }}>
              <CreditCardOutlined />
              Price Details
            </SectionTitle>

            <PricingRow>
              <span>Subtotal</span>
              <span className="value">
                ₹{(order.subtotal || 0).toLocaleString("en-IN")}
              </span>
            </PricingRow>

            <PricingRow>
              <span>Shipping Charges</span>
              <span className="value">
                ₹{(order.shipping || 0).toLocaleString("en-IN")}
              </span>
            </PricingRow>

            <PricingRow>
              <span>Tax</span>
              <span className="value">
                ₹{(order.tax || 0).toLocaleString("en-IN")}
              </span>
            </PricingRow>

            <PricingRow>
              <span>Order Total</span>
              <span className="value">
                ₹{(order.totalAmount || 0).toLocaleString("en-IN")}
              </span>
            </PricingRow>
          </PricingSummary>

          {/* Action Buttons */}
          <ActionButtons>
            <ActionButton onClick={handleDownloadInvoice}>
              <DownloadOutlined />
              Download Invoice
            </ActionButton>
            {isReturnWindowValid(order) ? (
              <ActionButton onClick={handleReturnModalOpen}>
                <UndoOutlined />
                Return
              </ActionButton>
            ) : null}
            <ActionButton secondary onClick={() => navigate("/orders")}>
              <ArrowLeftOutlined />
              Back to Orders
            </ActionButton>
          </ActionButtons>

          {/* Return/Replace Modal */}
          <ModalOverlay open={showReturnModal} onClick={handleReturnModalClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={handleReturnModalClose}>
                <CloseOutlined />
              </CloseButton>

              {/* Step 1: Choose Return or Replace */}
              {!returnFlow && (
                <>
                  <ModalTitle>Return or Replace Item</ModalTitle>
                  <ModalSubtitle>
                    Select whether you want to return or replace your item
                  </ModalSubtitle>
                  <OptionGroup>
                    <OptionButton
                      onClick={() => handleReturnFlow("return")}
                      active={returnFlow === "return"}
                    >
                      ↩️ Return Item
                    </OptionButton>
                    {/* <OptionButton
                      onClick={() => handleReturnFlow("replace")}
                      active={returnFlow === "replace"}
                    >
                      🔄 Replace Item
                    </OptionButton> */}
                  </OptionGroup>
                </>
              )}

              {/* Step 2: Return Item Form */}
              {returnFlow === "return" && (
                <>
                  <ModalTitle>Return Item</ModalTitle>

                  {order?.items && order.items.length > 1 && (
                    <FormGroup>
                      <FormLabel>Select Item to Return *</FormLabel>
                      <FormSelect
                        value={selectedItem?.id || ""}
                        onChange={(e) => {
                          const item = order.items.find(
                            (i) => i.id === parseInt(e.target.value),
                          );
                          setSelectedItem(item);
                        }}
                      >
                        <option value="">Choose item...</option>
                        {order.items.map((item) => {
                          const hasReturn = ordersReturns.some(
                            (ret) => ret.order_item_id === item.id,
                          );
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                              disabled={hasReturn}
                            >
                              {item.name} (Qty: {item.quantity})
                              {hasReturn ? " - Already Returned" : ""}
                            </option>
                          );
                        })}
                      </FormSelect>
                    </FormGroup>
                  )}

                  <FormGroup>
                    <FormLabel>Return Reason *</FormLabel>
                    <FormSelect
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                    >
                      <option value="">Select reason...</option>
                      <option value="defective">Defective Product</option>
                      <option value="not_as_described">Not as Described</option>
                      <option value="damaged">Damaged in Transit</option>
                      <option value="changed_mind">Changed My Mind</option>
                      <option value="other">Other</option>
                    </FormSelect>
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Details *</FormLabel>
                    <FormTextarea
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Please provide details about your return request..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      Upload Images (Max 4) * ({uploadedImages.length}/4)
                    </FormLabel>
                    <ImageUploadArea>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadedImages.length >= 4}
                        ref={(input) => {
                          if (input) input.id = "return-image-upload";
                        }}
                      />
                      <label htmlFor="return-image-upload">
                        <UploadOutlined style={{ fontSize: "24px" }} />
                        <ImageUploadText>
                          Click to upload or drag images here
                        </ImageUploadText>
                      </label>
                    </ImageUploadArea>

                    {uploadedImages.length > 0 && (
                      <ImagePreviewGrid>
                        {uploadedImages.map((image, index) => (
                          <ImagePreviewItem key={index}>
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Preview ${index}`}
                            />
                            <RemoveImageButton
                              onClick={() => handleRemoveImage(index)}
                            >
                              ×
                            </RemoveImageButton>
                          </ImagePreviewItem>
                        ))}
                      </ImagePreviewGrid>
                    )}
                  </FormGroup>

                  <ModalButtons>
                    <ModalButton onClick={() => setReturnFlow(null)}>
                      Back
                    </ModalButton>
                    <ModalButton primary onClick={handleSubmitReturn}>
                      Submit Return
                    </ModalButton>
                  </ModalButtons>
                </>
              )}

              {/* Step 2: Replace Item Form */}
              {returnFlow === "replace" && (
                <>
                  <ModalTitle>Replace Item</ModalTitle>

                  {order?.items && order.items.length > 1 && (
                    <FormGroup>
                      <FormLabel>Select Item to Replace *</FormLabel>
                      <FormSelect
                        value={selectedItem?.id || ""}
                        onChange={(e) => {
                          const item = order.items.find(
                            (i) => i.id === parseInt(e.target.value),
                          );
                          setSelectedItem(item);
                        }}
                      >
                        <option value="">Choose item...</option>
                        {order.items.map((item) => {
                          const hasReturn = ordersReturns.some(
                            (ret) => ret.order_item_id === item.id,
                          );
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                              disabled={hasReturn}
                            >
                              {item.name} (Qty: {item.quantity})
                              {hasReturn ? " - Already Returned" : ""}
                            </option>
                          );
                        })}
                      </FormSelect>
                    </FormGroup>
                  )}

                  {selectedItem && (
                    <ProductSelectionBox>
                      <div className="product-info">
                        <img src={selectedItem.image} alt={selectedItem.name} />
                        <div className="details">
                          <p>{selectedItem.name}</p>
                          <p className="meta">Qty: {selectedItem.quantity}</p>
                          {selectedItem.size && (
                            <p className="meta">Size: {selectedItem.size}</p>
                          )}
                          {selectedItem.color && (
                            <p className="meta">Color: {selectedItem.color}</p>
                          )}
                        </div>
                      </div>
                    </ProductSelectionBox>
                  )}

                  <FormGroup>
                    <FormLabel>Issue Type *</FormLabel>
                    <OptionGroup>
                      <OptionButton
                        active={replaceReason === "size_color"}
                        onClick={() => setReplaceReason("size_color")}
                      >
                        Size/Color Issue
                      </OptionButton>
                      <OptionButton
                        active={replaceReason === "damaged"}
                        onClick={() => setReplaceReason("damaged")}
                      >
                        Damaged Product
                      </OptionButton>
                    </OptionGroup>
                  </FormGroup>

                  {replaceReason === "size_color" && (
                    <>
                      <FormGroup>
                        <FormLabel>Select New Size *</FormLabel>
                        <FormSelect
                          value={selectedSize}
                          onChange={(e) => setSelectedSize(e.target.value)}
                          disabled={
                            productLoading || availableSizes.length === 0
                          }
                        >
                          <option value="">
                            {productLoading
                              ? "Loading sizes..."
                              : "Choose size..."}
                          </option>
                          {availableSizes.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </FormSelect>
                      </FormGroup>

                      <FormGroup>
                        <FormLabel>Select New Color *</FormLabel>
                        {availableColors.length === 0 && !productLoading && (
                          <div
                            style={{
                              padding: "10px 12px",
                              background: "#fff3cd",
                              border: "1px solid #ffc107",
                              borderRadius: "6px",
                              fontSize: "12px",
                              color: "#856404",
                              marginBottom: "8px",
                            }}
                          >
                            No colors available for this size. Please select a
                            different size.
                          </div>
                        )}
                        <FormSelect
                          value={selectedColor}
                          onChange={(e) => setSelectedColor(e.target.value)}
                          disabled={
                            productLoading || availableColors.length === 0
                          }
                        >
                          <option value="">
                            {productLoading
                              ? "Loading colors..."
                              : availableColors.length === 0
                                ? "No colors available"
                                : "Choose color..."}
                          </option>
                          {availableColors.map((color) => (
                            <option key={color} value={color}>
                              {color}
                            </option>
                          ))}
                        </FormSelect>
                      </FormGroup>
                    </>
                  )}

                  {replaceReason === "damaged" && (
                    <FormGroup>
                      <FormLabel>Describe the Damage *</FormLabel>
                      <FormTextarea
                        value={reasonText}
                        onChange={(e) => setReasonText(e.target.value)}
                        placeholder="Please describe the damage in detail..."
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <FormLabel>
                      Upload Images (Max 4) * ({replaceImages.length}/4)
                    </FormLabel>
                    <ImageUploadArea>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleReplaceImageUpload}
                        disabled={replaceImages.length >= 4}
                        ref={(input) => {
                          if (input) input.id = "replace-image-upload";
                        }}
                      />
                      <label htmlFor="replace-image-upload">
                        <UploadOutlined style={{ fontSize: "24px" }} />
                        <ImageUploadText>
                          Click to upload or drag images here
                        </ImageUploadText>
                      </label>
                    </ImageUploadArea>

                    {replaceImages.length > 0 && (
                      <ImagePreviewGrid>
                        {replaceImages.map((image, index) => (
                          <ImagePreviewItem key={index}>
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Preview ${index}`}
                            />
                            <RemoveImageButton
                              onClick={() => handleRemoveReplaceImage(index)}
                            >
                              ×
                            </RemoveImageButton>
                          </ImagePreviewItem>
                        ))}
                      </ImagePreviewGrid>
                    )}
                  </FormGroup>

                  <ModalButtons>
                    <ModalButton onClick={() => setReturnFlow(null)}>
                      Back
                    </ModalButton>
                    <ModalButton primary onClick={handleSubmitReplace}>
                      Submit Replacement
                    </ModalButton>
                  </ModalButtons>
                </>
              )}
            </ModalContainer>
          </ModalOverlay>

          {/* Review Modal */}
          <ModalOverlay
            open={isReviewModalOpen}
            onClick={() => setIsReviewModalOpen(false)}
          >
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={() => setIsReviewModalOpen(false)}>
                ✕
              </CloseButton>
              <ModalTitle>Write Your Review</ModalTitle>

              {reviewingItem && (
                <>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginBottom: "20px",
                    }}
                  >
                    {reviewingItem.productName}
                  </div>

                  {/* Rating Selector */}
                  <FormGroup>
                    <FormLabel>Rating</FormLabel>
                    <RatingSelector>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          onClick={() => setReviewRating(star)}
                          style={{
                            fontSize: "32px",
                            cursor: "pointer",
                            color: star <= reviewRating ? "#FFB800" : "#ddd",
                            marginRight: "8px",
                          }}
                        >
                          ★
                        </span>
                      ))}
                    </RatingSelector>
                  </FormGroup>

                  {/* Review Text */}
                  <FormGroup>
                    <FormLabel>Your Review</FormLabel>
                    <ReviewTextarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your thoughts about this product..."
                      rows="4"
                    />
                  </FormGroup>

                  {/* Image Upload */}
                  <FormGroup>
                    <FormLabel>
                      Add Photos (Optional) - {reviewImages.length}/4
                    </FormLabel>
                    <ImageUploadSection>
                      <ImageUploadInput
                        type="file"
                        id="reviewImageInput"
                        multiple
                        accept="image/*"
                        onChange={handleImageUploadChange}
                        disabled={reviewImages.length >= 4}
                        style={{ display: "none" }}
                      />
                      <ImageUploadButton
                        onClick={() =>
                          reviewImages.length < 4 &&
                          document.getElementById("reviewImageInput").click()
                        }
                        disabled={reviewImages.length >= 4}
                        style={{
                          opacity: reviewImages.length >= 4 ? 0.5 : 1,
                          cursor:
                            reviewImages.length >= 4
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {reviewImages.length >= 4
                          ? "Maximum Photos Reached"
                          : "+ Add Photos"}
                      </ImageUploadButton>
                    </ImageUploadSection>

                    {reviewImages.length > 0 && (
                      <ImagePreviewContainer>
                        {reviewImages.map((image, index) => (
                          <ImagePreview key={index}>
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Review ${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <ReviewRemoveImageButton
                              onClick={() => handleRemoveReviewImage(index)}
                            >
                              ×
                            </ReviewRemoveImageButton>
                          </ImagePreview>
                        ))}
                      </ImagePreviewContainer>
                    )}
                  </FormGroup>

                  {/* Buttons */}
                  <ModalButtons>
                    <ModalButton onClick={() => setIsReviewModalOpen(false)}>
                      Cancel
                    </ModalButton>
                    <ModalButton
                      primary
                      onClick={handleSubmitReview}
                      disabled={isSubmittingReview}
                    >
                      {isSubmittingReview ? "Submitting..." : "Submit Review"}
                    </ModalButton>
                  </ModalButtons>
                </>
              )}
            </ModalContainer>
          </ModalOverlay>
        </Wrapper>
      )}

      <Modal
        title="Review Images"
        open={isImagePreviewOpen}
        onCancel={() => setIsImagePreviewOpen(false)}
        footer={null}
        width={700}
        bodyStyle={{ padding: "32px 24px", background: "#f5f5f5" }}
      >
        {previewedReviewImages.length > 0 && (
          <SlideshowContainer>
            <div className="slideshow-wrapper">
              <img
                src={previewedReviewImages[selectedImageIndex]?.url}
                alt={`Image ${selectedImageIndex + 1}`}
                className="slide-image"
              />
              {previewedReviewImages.length > 1 && (
                <>
                  <button
                    className="nav-buttons nav-prev"
                    onClick={() =>
                      setSelectedImageIndex(
                        selectedImageIndex === 0
                          ? previewedReviewImages.length - 1
                          : selectedImageIndex - 1,
                      )
                    }
                  >
                    ❮
                  </button>
                  <button
                    className="nav-buttons nav-next"
                    onClick={() =>
                      setSelectedImageIndex(
                        selectedImageIndex === previewedReviewImages.length - 1
                          ? 0
                          : selectedImageIndex + 1,
                      )
                    }
                  >
                    ❯
                  </button>
                </>
              )}
            </div>
            {previewedReviewImages.length > 1 && (
              <div
                className="dot-indicators"
                style={{ position: "absolute", bottom: "15px", width: "100%" }}
              >
                {previewedReviewImages.map((_, idx) => (
                  <div
                    key={idx}
                    className={`dot ${idx === selectedImageIndex ? "active" : ""}`}
                    onClick={() => setSelectedImageIndex(idx)}
                  />
                ))}
              </div>
            )}
          </SlideshowContainer>
        )}
      </Modal>

      <Footer />
    </Container>
  );
};

export default OrderDetails;
